/**
 * Admin Management API Service
 * Handles CRUD operations for admin accounts
 */

import axios from 'axios';
import { getAdminEndpoint } from '../endpoints/adminEndpointManager';
import { getAdminToken } from '../auth/authManager';
import { getEnvironmentConfig } from '../config/apiConfig';

// Get auth service endpoint from stored configuration
const getAuthServiceEndpoint = () => {
  // First try to get from environment config (this is the auth service URL selected during login)
  const config = getEnvironmentConfig();
  if (config?.authServiceEndpoint) {
    return config.authServiceEndpoint;
  }
  // Fallback to admin endpoint
  return getAdminEndpoint();
};

// Create axios instance for admin management
const createAdminClient = () => {
  return axios.create({
    baseURL: `${getAuthServiceEndpoint()}/api/admin`,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 10000,
  });
};

// Get auth headers with token
const getAuthHeaders = () => {
  const token = getAdminToken();
  return {
    Authorization: `Bearer ${token}`,
  };
};

export const adminManagementAPI = {
  /**
   * Get all admins
   * Required permission: admins.read
   */
  getAllAdmins: async () => {
    try {
      const client = createAdminClient();
      const response = await client.get('/admins', {
        headers: getAuthHeaders()
      });
      
      // Handle nested response format from backend
      const data = response.data;
      if (data.data && Array.isArray(data.data.admins)) {
        // Backend returns { success: true, data: { admins: [...], total: n } }
        return {
          admins: data.data.admins,
          total: data.data.total
        };
      }
      
      // Fallback to direct format if backend returns { admins: [...], total: n }
      return {
        admins: data.admins || [],
        total: data.total || 0
      };
    } catch (error) {
      console.error('Failed to get all admins:', error);
      throw error;
    }
  },

  /**
   * Get single admin by ID
   * Required permission: admins.read
   */
  getAdminById: async (adminId) => {
    try {
      const client = createAdminClient();
      const response = await client.get(`/admins/${adminId}`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to get admin ${adminId}:`, error);
      throw error;
    }
  },

  /**
   * Create new admin
   * Required permission: admins.create
   */
  createAdmin: async (adminData) => {
    try {
      const client = createAdminClient();
      const response = await client.post('/admins', {
        username: adminData.username,
        email: adminData.email,
        password: adminData.password,
        role: adminData.role || 'admin',
        firstName: adminData.firstName,
        lastName: adminData.lastName
      }, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to create admin:', error);
      throw error;
    }
  },

  /**
   * Update admin
   * Required permission: admins.update
   */
  updateAdmin: async (adminId, updateData) => {
    try {
      const client = createAdminClient();
      const response = await client.put(`/admins/${adminId}`, updateData, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to update admin ${adminId}:`, error);
      throw error;
    }
  },

  /**
   * Change admin role
   * Required permission: system.super (super admin only)
   */
  changeAdminRole: async (adminId, role) => {
    try {
      const client = createAdminClient();
      const response = await client.put(`/admins/${adminId}/role`, { role }, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to change role for admin ${adminId}:`, error);
      throw error;
    }
  },

  /**
   * Reset admin password
   * Required permission: system.super (super admin only)
   */
  resetAdminPassword: async (adminId, newPassword) => {
    try {
      const client = createAdminClient();
      const response = await client.post(`/admins/${adminId}/reset-password`, 
        { newPassword },
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to reset password for admin ${adminId}:`, error);
      throw error;
    }
  },

  /**
   * Delete admin
   * Required permission: admins.delete
   */
  deleteAdmin: async (adminId) => {
    try {
      const client = createAdminClient();
      const response = await client.delete(`/admins/${adminId}`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to delete admin ${adminId}:`, error);
      throw error;
    }
  },

  /**
   * Get available roles
   */
  getAvailableRoles: () => {
    return [
      { value: 'super_admin', label: 'Super Admin', description: 'Full access to all features' },
      { value: 'admin', label: 'Admin', description: 'Can manage users, endpoints, and most settings' },
      { value: 'moderator', label: 'Moderator', description: 'Can view and update users, view endpoints' },
      { value: 'viewer', label: 'Viewer', description: 'Read-only access' }
    ];
  },

  /**
   * Get role permissions
   */
  getRolePermissions: (role) => {
    const permissions = {
      super_admin: [
        'users.read', 'users.create', 'users.update', 'users.delete', 'users.block',
        'admins.read', 'admins.create', 'admins.update', 'admins.delete', 'admins.assign_role',
        'endpoints.read', 'endpoints.create', 'endpoints.update', 'endpoints.delete',
        'settings.read', 'settings.update', 'settings.maintenance', 'system.super'
      ],
      admin: [
        'users.read', 'users.create', 'users.update', 'users.delete', 'users.block',
        'admins.read',
        'endpoints.read', 'endpoints.create', 'endpoints.update', 'endpoints.delete',
        'settings.read', 'settings.update'
      ],
      moderator: [
        'users.read', 'users.update', 'users.block',
        'admins.read',
        'endpoints.read'
      ],
      viewer: [
        'users.read',
        'admins.read',
        'endpoints.read',
        'settings.read'
      ]
    };
    return permissions[role] || [];
  }
};

export default adminManagementAPI;
